import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Wallet, ArrowDownToLine, User, LogOut, Sun, Moon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { TiplymeLogo } from "@/components/Brand";
import { InstallButton } from "@/components/InstallButton";
import { Button } from "@/components/ui/button";

const tabs = [
  { to: "/app", end: true, icon: LayoutDashboard, label: "Home", tid: "tab-home" },
  { to: "/app/wallet", icon: Wallet, label: "Wallet", tid: "tab-wallet" },
  { to: "/app/cashout", icon: ArrowDownToLine, label: "Cashout", tid: "tab-cashout" },
  { to: "/app/profile", icon: User, label: "Profile", tid: "tab-profile" },
];

export default function WorkerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("tiplyme-worker-theme") === "dark");

  useEffect(() => {
    const el = document.documentElement;
    if (dark) el.classList.add("dark"); else el.classList.remove("dark");
    return () => el.classList.remove("dark");
  }, [dark]);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    localStorage.setItem("tiplyme-worker-theme", next ? "dark" : "light");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-card/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-lg items-center justify-between px-5 py-3">
          <TiplymeLogo />
          <div className="flex items-center gap-2">
            <div className="mr-1 text-right">
              <p className="text-sm font-semibold leading-tight">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.workplace_name || "Worker"}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="worker-theme-toggle" className="rounded-full" aria-label="Toggle dark mode">
              {dark ? <Sun className="h-4 w-4 text-brand-orange" /> : <Moon className="h-4 w-4" />}
            </Button>
            <InstallButton dark={dark} />
            <Button variant="ghost" size="icon" onClick={async () => { await logout(); navigate("/login"); }} data-testid="worker-logout-button" className="rounded-full">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-5 pb-28 pt-6">
        <Outlet />
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-card/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-lg items-stretch justify-around px-2 py-2">
          {tabs.map((t) => (
            <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.tid}
              className={({ isActive }) => `flex flex-1 flex-col items-center gap-1 rounded-xl py-2 text-xs font-semibold transition-colors ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
              {({ isActive }) => (<><t.icon className={`h-5 w-5 ${isActive ? "scale-110" : ""} transition-transform`} />{t.label}</>)}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
